import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Ruler, Activity, Users, TrendingUp, Gauge, Scale, FlaskConical, Apple, Moon, Sun,
  MonitorOff, UtensilsCrossed, HeartPulse, Phone, ChevronRight, ArrowUpRight, Check,
  CalendarClock, Dna, Info, Bike,
} from "lucide-react";
import Navbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/83ea495c-8bdb-4889-9a46-037ad22cb3df/images/0e1cc0e7632df3e95ba6bfcc866fde23a8bd9b019995a33f3252f067340f1392.jpeg";
const TEAL = "#4ECDC4";
const CORAL = "#FF6B6B";
const YELLOW = "#FFE66D";
const VIOLET = "#A855F7";
const PHONE = "+91 8985709090";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "show" : "hidden"}
      variants={fadeUp} transition={{ delay }} className={className}>
      {children}
    </motion.div>
  );
};

const STEPS = [
  { n: "01", id: "step-1", label: "Understand growth", color: TEAL },
  { n: "02", id: "step-2", label: "Blood tests", color: CORAL },
  { n: "03", id: "step-3", label: "Food plan", color: YELLOW },
  { n: "04", id: "step-4", label: "Everyday habits", color: VIOLET },
];

const ASSESS = [
  { icon: Ruler, text: "Height and weight, plotted against Indian (IAP) growth charts" },
  { icon: Activity, text: "Bone age: shows how mature your child's bones are compared with their actual age, and how much growing time is likely left" },
  { icon: Users, text: "Parents' heights: used to work out your child's genetic target height range" },
  { icon: TrendingUp, text: "Predicted adult height and the expected timing of the growth spurt" },
  { icon: Gauge, text: "Growth Health Index: a combined score of height, BMI and maturity" },
  { icon: Scale, text: "Weight and BMI for age" },
];

const LEARN = [
  "Is your child growing at a healthy pace?",
  "Is your child's bone age ahead of, behind, or in line with their age?",
  "What height range do your family's genetics point to?",
  "How much growing time is left, and when is the spurt expected?",
];

const BLOOD_TESTS = [
  ["Haemoglobin and iron stores (CBC, ferritin)", "Low iron affects energy, appetite and growth"],
  ["Vitamin B12", "Important for growth, especially for vegetarian children"],
  ["Vitamin D, calcium, phosphate", "The building blocks of strong bones"],
  ["Thyroid tests (TSH, free T4)", "The thyroid gland helps control growth"],
  ["Growth marker (IGF-1)", "Reflects how the body's growth hormone system is working"],
  ["Gut and absorption markers (such as a celiac screen)", "Show whether nutrients are being absorbed properly"],
  ["Blood sugar and cholesterol", "Advised for children with higher weight"],
];

const NUTRIENTS = [
  ["Daily energy and carbohydrates (calories, whole grains and millets)", "Steady daily energy for growth and play", "Ragi, jowar, bajra, brown rice, rice, wheat"],
  ["Protein at every meal", "Builds muscle, bone and body tissue", "Dals, pulses, sprouts, milk, curd, paneer, soy, groundnuts; eggs, fish or chicken as per preference"],
  ["Fibre", "Healthy digestion and steady appetite", "Whole grains, millets, pulses, fruits and vegetables with skin"],
  ["Healthy fats and essential fatty acids (DHA omega-3)", "Brain growth and absorption of fat-soluble vitamins", "Nuts, seeds, fresh coconut, a little ghee; fish, or an algae-based DHA supplement for vegetarians on a doctor's advice"],
  ["Water-soluble vitamins (B1, B2, B3, B5, B6, B7, folate, B12, vitamin C, choline)", "Energy release, blood formation, immunity and iron absorption", "B group: whole grains, pulses, groundnuts, milk. Folate: leafy greens, dals. B12: milk, curd, paneer, eggs, fish. Vitamin C: guava, amla, lemon, orange, papaya. Choline: eggs, soy, groundnuts, milk"],
  ["Fat-soluble vitamins (A, D, E, K)", "Vision, bone building, immunity and blood clotting", "A: carrot, papaya, mango, spinach, eggs. D: morning sunlight, eggs, fish, fortified foods. E: nuts, seeds, groundnuts. K: leafy greens, curry leaves"],
  ["Minerals: calcium, magnesium, phosphorus, sodium, potassium", "Strong bones and teeth, muscle and nerve function, fluid balance", "Calcium: milk, curd, paneer, ragi, sesame, drumstick, curry leaves. Magnesium and phosphorus: nuts, whole grains, pulses, dairy. Potassium: banana, tomato, coconut water. Sodium: iodised salt in small amounts"],
  ["Trace elements: iron, zinc, copper, iodine, selenium, chromium, manganese", "Healthy blood, growth, immunity and thyroid function", "Iron with vitamin C: ragi, dals, leafy greens, jaggery, with guava, lemon, amla or orange. Zinc: pulses, nuts, seeds, whole grains, dairy. Iodine: iodised salt, milk. Copper, selenium, chromium and manganese: whole grains, nuts, seeds, pulses"],
  ["Antioxidants: beta carotene, lycopene, lutein", "Protect body cells and support eye health", "Beta carotene: carrot, papaya, mango, greens. Lycopene: tomato, guava. Lutein: spinach, drumstick leaves and other green vegetables"],
  ["Water", "Hydration and digestion", "Plain water as the main drink"],
  ["Everyday recommendations", "Good habits help the body use the nutrients", "Regular meal timing, colourful vegetables, enough sleep, limited screen time and active play"],
];

const ADJUST = [
  { title: "If weight is high", body: "The variety stays, with guidance on portion sizes and less sugary drinks, fried snacks and packaged food. Children are never put on crash diets.", color: CORAL },
  { title: "If weight is low", body: "We add energy- and protein-rich foods and smaller, more frequent meals.", color: YELLOW },
  { title: "If blood tests show a deficiency", body: "The plan targets it, and supplements are suggested only when your doctor advises them.", color: TEAL },
  { title: "Vegetarian children", body: "Extra attention to B12, iron, zinc and protein.", color: VIOLET },
];

const HABITS = [
  { icon: Moon, title: "Sleep", body: "Growth hormone is released mostly during deep sleep. Children aged 6–12 need 9–12 hours, with a fixed bedtime.", color: VIOLET },
  { icon: Bike, title: "Active play", body: "At least 60 minutes a day: running, cycling, swimming, skipping, football, badminton.", color: TEAL },
  { icon: Sun, title: "Sunlight", body: "Time outdoors most days to support vitamin D.", color: YELLOW },
  { icon: MonitorOff, title: "Screen time", body: "Limit recreational screens, and avoid them in the hour before bed.", color: CORAL },
  { icon: UtensilsCrossed, title: "Regular meals", body: "Fixed meal times, no force-feeding, and eating together as a family.", color: TEAL },
  { icon: HeartPulse, title: "Healthy weight", body: "Neither underweight nor overweight, since both can affect growth and puberty timing.", color: CORAL },
];

const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const bookNow = () => {
  sessionStorage.setItem("scrollToOnLoad", "contact");
  window.location.href = "/#contact";
};

const Eyebrow = ({ children, color = TEAL }) => (
  <p className="text-xs font-semibold tracking-[0.18em] uppercase mb-3" style={{ color }}>{children}</p>
);

const StepHeader = ({ n, color, title, subtitle }) => (
  <Reveal className="mb-12 max-w-3xl">
    <div className="flex items-center gap-4 mb-5">
      <span className="font-display text-5xl md:text-6xl font-bold leading-none" style={{ color }}>{n}</span>
      <span className="h-px w-16" style={{ background: `${color}80` }} />
      <span className="text-xs font-semibold tracking-[0.18em] uppercase text-white/50">Step {Number(n)}</span>
    </div>
    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] mb-4">{title}</h2>
    {subtitle && <p className="text-white/55 text-base md:text-lg leading-relaxed">{subtitle}</p>}
  </Reveal>
);

const TryTool = ({ to, label, color, testId }) => (
  <Link to={to} data-testid={testId}
    className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-300 hover:bg-white/[0.06]"
    style={{ borderColor: `${color}55`, color }}>
    Try the tool: {label}
    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </Link>
);

const Hero = () => (
  <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-testid="he-hero">
    <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#4ECDC4]/15 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#FFE66D]/10 blur-3xl pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12 items-center">
      <Reveal className="lg:col-span-3">
        <div className="badge-glass inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium mb-6" data-testid="he-eyebrow">
          <Ruler className="w-3.5 h-3.5 text-[#4ECDC4]" />
          <span>Height Enhancement Program</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6" data-testid="he-title">
          Do you want your child to grow{" "}
          <span className="text-[#4ECDC4]">taller than you?</span>
        </h1>
        <p className="text-white/60 text-base md:text-lg leading-relaxed mb-4 max-w-2xl">
          Understand your child's growth potential through personalized height assessment, adult-height
          prediction, nutrition insights, and regular growth monitoring.
        </p>
        <p className="text-white font-medium text-base md:text-lg mb-8 max-w-2xl">
          A science-based plan to help your child reach their full growth potential.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={bookNow} className="btn-coral px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2" data-testid="he-hero-book-btn">
            Book Now <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => jump("step-1")} className="btn-glass px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 text-white" data-testid="he-hero-explore-btn">
            See the 4-step plan
          </button>
        </div>
      </Reveal>
      <Reveal className="lg:col-span-2" delay={0.15}>
        <div className="relative aspect-square w-full rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-[#4ECDC4]/10">
          <img src={HERO_IMG} alt="Mother measuring her child's height against a wall chart" className="absolute inset-0 w-full h-full object-cover" data-testid="he-hero-img" />
          <div className="absolute bottom-0 left-0 right-0 px-5 py-4 bg-gradient-to-t from-[#0A1029] via-[#0A1029]/80 to-transparent">
            <p className="text-xs uppercase tracking-[0.18em] text-white/55">Personalised, not generic</p>
            <p className="font-display text-xl md:text-2xl text-white leading-snug">Built on your child's growth and family background.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const GeneticsIntro = () => (
  <section className="relative py-20 md:py-24 px-6" data-testid="he-genetics">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <Eyebrow>What decides height</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl text-white leading-[1.1] mb-5">Genes set the range. Daily life decides how much of it your child reaches.</h2>
        <p className="text-white/60 leading-relaxed mb-4">
          Height is influenced by genetics, which accounts for approximately 70–80% of height variation.
          The remaining 20–30% reflects the influence of other factors, including nutrition, sleep, physical
          activity, and overall health.
        </p>
        <p className="text-white/60 leading-relaxed">
          With the right personalized plan, you can help your child make the most of their natural growth
          potential. Our 4-step Height Enhancement Program is tailored to your child's growth patterns and
          family background, not based on a generic chart.
        </p>
      </Reveal>
      <Reveal delay={0.1} className="glass-card rounded-3xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-3 text-sm">
          <span className="inline-flex items-center gap-2 text-white"><Dna className="w-4 h-4 text-[#A855F7]" /> Genetics</span>
          <span className="font-display text-2xl text-[#A855F7]" data-testid="he-genetics-pct">70–80%</span>
        </div>
        <div className="h-4 rounded-full bg-white/5 overflow-hidden flex mb-3">
          <motion.div initial={{ width: 0 }} whileInView={{ width: "75%" }} viewport={{ once: true }} transition={{ duration: 1.1, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-[#A855F7] to-[#7C3AED]" />
          <motion.div initial={{ width: 0 }} whileInView={{ width: "25%" }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-[#4ECDC4] to-[#FFE66D]" />
        </div>
        <div className="flex items-center justify-between mb-8 text-sm">
          <span className="text-white/50">What you inherit</span>
          <span className="inline-flex items-center gap-2 text-white">What you can influence <span className="font-display text-2xl text-[#4ECDC4]" data-testid="he-lifestyle-pct">20–30%</span></span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[[Apple, "Nutrition", CORAL], [Moon, "Sleep", VIOLET], [Bike, "Physical activity", TEAL], [HeartPulse, "Overall health", YELLOW]].map(([Icon, label, c]) => (
            <div key={label} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <Icon className="w-4 h-4" style={{ color: c }} />
              <span className="text-sm text-white/75">{label}</span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const StepChips = () => (
  <div className="sticky top-[68px] z-30 bg-[#0A1029]/85 backdrop-blur-xl border-y border-white/5">
    <div className="max-w-7xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="he-step-nav">
      {STEPS.map((s) => (
        <button key={s.id} onClick={() => jump(s.id)} data-testid={`he-step-nav-${s.n}`}
          className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border border-white/10 hover:border-white/30 hover:text-white text-white/55 transition-colors">
          <span className="font-semibold mr-1.5" style={{ color: s.color }}>{s.n}</span>{s.label}
        </button>
      ))}
      <button onClick={() => jump("track-progress")} data-testid="he-step-nav-progress"
        className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border border-white/10 hover:border-white/30 hover:text-white text-white/55 transition-colors">
        Track progress
      </button>
    </div>
  </div>
);

const StepOne = () => (
  <section id="step-1" className="relative py-20 md:py-28 px-6 scroll-mt-28" data-testid="he-step-1">
    <div className="max-w-7xl mx-auto">
      <StepHeader n="01" color={TEAL} title="Understand your child's growth"
        subtitle="We start by finding where your child stands today and what their family genetics suggest." />
      <div className="grid lg:grid-cols-5 gap-6">
        <Reveal className="lg:col-span-3 glass-card rounded-3xl p-6 md:p-8">
          <h3 className="font-display text-xl text-white mb-6">What we assess</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {ASSESS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`he-assess-item-${i}`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-[#4ECDC4]/15">
                  <Icon className="w-4 h-4 text-[#4ECDC4]" />
                </div>
                <p className="text-sm text-white/70 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-2 glass-card rounded-3xl p-6 md:p-8 flex flex-col">
          <h3 className="font-display text-xl text-white mb-6">What you learn</h3>
          <ul className="space-y-4 flex-1">
            {LEARN.map((q, i) => (
              <li key={i} className="flex gap-3" data-testid={`he-learn-item-${i}`}>
                <span className="font-mono text-xs text-[#4ECDC4] pt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/80 leading-relaxed">{q}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3 mt-8">
            <TryTool to="/tools/growth" label="Growth Assessment" color={YELLOW} testId="he-try-growth-tool" />
            <TryTool to="/tools/bone-age" label="Bone Age" color={TEAL} testId="he-try-bone-age-tool" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const StepTwo = () => (
  <section id="step-2" className="relative py-20 md:py-28 px-6 scroll-mt-28 bg-white/[0.01] border-y border-white/5" data-testid="he-step-2">
    <div className="max-w-7xl mx-auto">
      <StepHeader n="02" color={CORAL} title="Blood tests to see what's happening with growth factors"
        subtitle="Simple blood tests show whether something is quietly holding growth back. Which tests are advised depends on your child's growth assessment, and your child's doctor reviews every result with you." />
      <Reveal className="glass-card rounded-3xl overflow-hidden">
        <div className="hidden md:grid grid-cols-2 gap-6 px-8 py-4 border-b border-white/10 text-xs uppercase tracking-[0.15em] text-white/45">
          <span>What we look at</span><span>Why it matters</span>
        </div>
        {BLOOD_TESTS.map(([test, why], i) => (
          <div key={i} className="grid md:grid-cols-2 gap-1 md:gap-6 px-6 md:px-8 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition-colors" data-testid={`he-blood-test-row-${i}`}>
            <span className="flex items-start gap-3 text-white font-medium">
              <FlaskConical className="w-4 h-4 mt-1 shrink-0 text-[#FF6B6B]" />{test}
            </span>
            <span className="text-white/60 text-sm md:text-base pl-7 md:pl-0">{why}</span>
          </div>
        ))}
      </Reveal>
      <Reveal className="mt-6 flex items-start gap-3 text-sm text-white/55">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#FF6B6B]" />
        <p data-testid="he-blood-test-note">Further tests are suggested only if the first results or the growth pattern call for them.</p>
      </Reveal>
    </div>
  </section>
);

const NutrientTable = () => (
  <Reveal className="glass-card rounded-3xl overflow-hidden">
    <div className="hidden lg:grid grid-cols-[1.1fr_1fr_1.6fr] gap-6 px-8 py-4 border-b border-white/10 text-xs uppercase tracking-[0.15em] text-white/45">
      <span>Food group and nutrient category</span><span>Why it's in the plan</span><span>Examples</span>
    </div>
    {NUTRIENTS.map(([group, why, ex], i) => (
      <div key={i} className="grid lg:grid-cols-[1.1fr_1fr_1.6fr] gap-2 lg:gap-6 px-6 lg:px-8 py-5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition-colors" data-testid={`he-nutrient-row-${i}`}>
        <span className="text-white font-medium">{group}</span>
        <span className="text-[#FFE66D]/85 text-sm">{why}</span>
        <span className="text-white/60 text-sm leading-relaxed">{ex}</span>
      </div>
    ))}
  </Reveal>
);

const StepThree = () => (
  <section id="step-3" className="relative py-20 md:py-28 px-6 scroll-mt-28" data-testid="he-step-3">
    <div className="max-w-7xl mx-auto">
      <StepHeader n="03" color={YELLOW} title="A food plan built for your child"
        subtitle="No two children need the same diet. We use your child's age, weight, height, bone age, food preference (vegetarian or non-vegetarian), region and blood test results to set how many calories your child needs, how much protein, calcium, iron, zinc, vitamins and healthy fats to aim for, which familiar regional foods to choose, and how much of each, in simple portions." />
      <Reveal className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <h3 className="font-display text-xl md:text-2xl text-white mb-2">What your child's nutrition report covers</h3>
          <p className="text-white/55 text-sm md:text-base">Every plan sets daily targets across these categories, with amounts matched to your child's age, weight and growth results.</p>
        </div>
        <TryTool to="/tools/nin-nutriguide" label="NIN-NutriGuide" color={CORAL} testId="he-try-nutriguide-tool" />
      </Reveal>
      <NutrientTable />
      <Reveal className="mt-14 mb-6"><h3 className="font-display text-xl md:text-2xl text-white">Adjusted to your child's results</h3></Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {ADJUST.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.06} className="glass-card rounded-2xl p-6">
            <div data-testid={`he-adjust-card-${i}`}>
              <span className="block w-8 h-1 rounded-full mb-4" style={{ background: a.color }} />
              <h4 className="font-display text-lg text-white mb-2">{a.title}</h4>
              <p className="text-sm text-white/60 leading-relaxed">{a.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const StepFour = () => (
  <section id="step-4" className="relative py-20 md:py-28 px-6 scroll-mt-28 bg-white/[0.01] border-y border-white/5" data-testid="he-step-4">
    <div className="max-w-7xl mx-auto">
      <StepHeader n="04" color={VIOLET} title="Everyday habits that support growth"
        subtitle="Food works best alongside the right daily routine." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {HABITS.map(({ icon: Icon, title, body, color }, i) => (
          <Reveal key={title} delay={i * 0.05}>
            <div className="glass-card rounded-2xl p-6 h-full group transition-transform duration-300 hover:-translate-y-1" data-testid={`he-habit-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${color}1f` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <h4 className="font-display text-lg text-white mb-2">{title}</h4>
              <p className="text-sm text-white/60 leading-relaxed">{body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TrackProgress = () => (
  <section id="track-progress" className="relative py-20 md:py-24 px-6 scroll-mt-28" data-testid="he-track-progress">
    <Reveal className="max-w-7xl mx-auto glass-card rounded-3xl p-8 md:p-12 grid md:grid-cols-[auto_1fr] gap-8 items-center">
      <div className="w-24 h-24 md:w-28 md:h-28 rounded-3xl flex flex-col items-center justify-center bg-[#4ECDC4]/10 border border-[#4ECDC4]/30">
        <CalendarClock className="w-6 h-6 text-[#4ECDC4] mb-1" />
        <span className="font-display text-3xl text-white leading-none">3</span>
        <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">months</span>
      </div>
      <div>
        <Eyebrow>Track progress, step by step</Eyebrow>
        <p className="text-white/75 text-base md:text-lg leading-relaxed">
          We repeat height and weight measurements every 3 months and review the plan with you. If your
          child's growth slows, drops across percentile lines, or puberty starts very early or late, our
          paediatricians will guide you on the next step.
        </p>
      </div>
    </Reveal>
  </section>
);

const ClosingCTA = () => (
  <section className="relative pb-16 px-6" data-testid="he-cta">
    <Reveal className="max-w-7xl mx-auto relative glass-card rounded-3xl p-8 md:p-14 text-center overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-[#4ECDC4]/10 blur-3xl pointer-events-none" />
      <p className="font-display text-3xl md:text-5xl text-white mb-3 relative">Ready to begin?</p>
      <p className="text-white/60 text-base md:text-lg mb-8 relative">Book your child's growth screening today.</p>
      <div className="flex flex-wrap items-center justify-center gap-3 relative">
        <button onClick={bookNow} className="btn-coral px-7 py-3 rounded-full font-semibold inline-flex items-center gap-2" data-testid="he-cta-book-btn">
          Book Now <ChevronRight className="w-4 h-4" />
        </button>
        <a href="tel:+918985709090" className="btn-glass px-7 py-3 rounded-full font-semibold inline-flex items-center gap-2 text-white" data-testid="he-cta-call-btn">
          <Phone className="w-4 h-4" /> Call {PHONE}
        </a>
      </div>
    </Reveal>
  </section>
);

const Disclaimer = () => (
  <section className="px-6 pb-20" data-testid="he-disclaimer">
    <div className="max-w-7xl mx-auto flex gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6">
      <Info className="w-4 h-4 mt-0.5 shrink-0 text-white/40" />
      <p className="text-xs md:text-sm text-white/45 leading-relaxed">
        <span className="font-semibold text-white/60">Disclaimer: </span>
        This programme supports healthy growth and helps children reach their genetic potential. It does not
        promise a specific increase in height. A child's height depends largely on genetics, and it can be
        supported through good nutrition and monitoring of health conditions. Our expert doctors will guide
        you through the process and help you achieve the best possible results.
      </p>
    </div>
  </section>
);

const HeightEnhancementPage = () => (
  <div className="min-h-screen bg-[#0A1029] text-white" data-testid="height-enhancement-page">
    <Navbar />
    <Hero />
    <GeneticsIntro />
    <StepChips />
    <StepOne />
    <StepTwo />
    <StepThree />
    <StepFour />
    <TrackProgress />
    <ClosingCTA />
    <Disclaimer />
    <Footer />
  </div>
);

export default HeightEnhancementPage;
